import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { useFirebase } from './FirebaseContext';
import { getFirestore, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { UserData } from '../types/firebase';
interface AuthContextType {
  currentUser: UserData | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  createUserProfile: (userData: Partial<UserData>, user?: UserData | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const { app } = useFirebase();
  const auth = getAuth(app);

  const signup = async (email: string, password: string) => {
    return createUserWithEmailAndPassword(auth, email, password)
      .then(async ({ user }) => {
        setCurrentUser(user as UserData);
        await createUserProfile({}, user as UserData);
      })
      .catch((error) => {
        console.error('Signup error:', error);
        throw error;
      });
  };

  const login = async (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then(({ user }) => {
        setCurrentUser(user as UserData);
      })
      .catch((error) => {
        throw error;
      });
  };

  const logout = async () => {
    try {
      setCurrentUser(null);
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider)
      .then(async ({ user }) => {
        const db = getFirestore(app);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          await createUserProfile({}, user as UserData);
        }
      })
      .catch((error) => {
        throw error;
      });
  };

  const createUserProfile = async (userData: Partial<UserData>, user: UserData | null = currentUser) => {
    if (!user) {
      throw new Error('No authenticated user');
    }
    
    // Create base user data without undefined values
    const userDocData: Partial<UserData> = {
      uid: user.uid,
      email: user.email || null,  // Convert undefined to null if email doesn't exist
      ...userData,
      createdAt: new Date().toISOString(),
    };

    // Only add displayName if it exists
    if (user.displayName) {
      userDocData.displayName = user.displayName;
    }
    
    const db = getFirestore(app);
    try {
      await setDoc(doc(db, 'users', user.uid), userDocData);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    let unsubscribeFirestore: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
        unsubscribeFirestore = undefined;
      }

      try {
        if (user) {
          const db = getFirestore(app);
          const userDocRef = doc(db, 'users', user.uid);
          
          unsubscribeFirestore = onSnapshot(userDocRef, (doc) => {
            const userData = {
              ...user,
              ...doc.data()
            } as UserData;
            setCurrentUser(userData);
          });
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) {
        unsubscribeFirestore();
      }
    };
  }, [auth, app]);

  const value = {
    currentUser,
    loading,
    signup,
    login,
    loginWithGoogle,
    logout,
    createUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 